import { useEffect, useState } from "react";
import { Repeat, X, RefreshCw, AlertCircle, Calendar } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const STATUS_COLORS = {
  ACTIVE: "bg-emerald-100 text-emerald-800",
  APPROVAL_PENDING: "bg-amber-100 text-amber-800",
  APPROVED: "bg-blue-100 text-blue-800",
  SUSPENDED: "bg-orange-100 text-orange-800",
  CANCELLED: "bg-gray-200 text-gray-700",
  EXPIRED: "bg-gray-200 text-gray-700",
};

const SubscriptionsManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/subscriptions");
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const cancel = async (item) => {
    if (
      !window.confirm(
        `Annuler la souscription de ${item.donor_name} (${item.amount} ${item.currency}/mois) ?\n\nCette action est immédiate et irréversible.`
      )
    )
      return;
    setCancelling(item.paypal_subscription_id);
    try {
      await api.post(`/admin/subscriptions/${item.paypal_subscription_id}/cancel`);
      toast.success("Souscription annulée");
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCancelling(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "—";
    }
  };

  const stats = items.reduce(
    (acc, it) => {
      acc.total++;
      if (it.paypal_status === "ACTIVE") acc.active++;
      acc.mrr += it.paypal_status === "ACTIVE" ? Number(it.amount || 0) : 0;
      return acc;
    },
    { total: 0, active: 0, mrr: 0 }
  );

  return (
    <div data-testid="subscriptions-manager">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <KpiSmall label="Souscriptions" value={stats.total} testid="sub-kpi-total" />
        <KpiSmall label="Actives" value={stats.active} testid="sub-kpi-active" />
        <KpiSmall
          label="Revenu mensuel récurrent"
          value={`${stats.mrr.toFixed(2)} €`}
          testid="sub-kpi-mrr"
        />
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#64748B]">
          Liste de tous les engagements mensuels via PayPal. La date du prochain prélèvement est récupérée en direct.
        </p>
        <button
          onClick={fetchAll}
          className="text-[#64748B] hover:text-[#002B49] inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md hover:bg-black/5"
          data-testid="sub-refresh"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualiser
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-[#64748B]">Chargement...</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-[#64748B]" data-testid="no-subscriptions">
          <Repeat size={28} className="text-[#C5A059] mx-auto mb-3 opacity-50" />
          Aucune souscription pour l'instant.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FDFBF7] border-b border-black/5">
                <tr className="text-left text-[#64748B] uppercase text-[11px] tracking-wider">
                  <th className="px-5 py-3 font-medium">Donateur</th>
                  <th className="px-5 py-3 font-medium">Montant</th>
                  <th className="px-5 py-3 font-medium">Statut</th>
                  <th className="px-5 py-3 font-medium">Souscrit le</th>
                  <th className="px-5 py-3 font-medium">Prochain prélèv.</th>
                  <th className="px-5 py-3 font-medium">Sub ID</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const status = it.paypal_status || it.paypal_subscription_status || "—";
                  const isTerminal = ["CANCELLED", "EXPIRED"].includes(status);
                  return (
                    <tr
                      key={it.id}
                      className="border-b border-black/5 last:border-0 hover:bg-[#FDFBF7]"
                      data-testid={`sub-row-${i}`}
                    >
                      <td className="px-5 py-4">
                        <div className="font-medium text-[#0A192F]">
                          {it.anonymous ? "Anonyme" : it.donor_name}
                        </div>
                        <div className="text-xs text-[#64748B]">{it.donor_email}</div>
                      </td>
                      <td className="px-5 py-4 font-serif text-lg font-semibold text-[#002B49] whitespace-nowrap">
                        {Number(it.amount).toFixed(2)} {it.currency}/mois
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                            STATUS_COLORS[status] || "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-[#64748B] whitespace-nowrap">
                        {fmtDate(it.created_at)}
                      </td>
                      <td className="px-5 py-4 text-[#64748B] whitespace-nowrap">
                        {it.next_billing_time ? (
                          <span className="inline-flex items-center gap-1.5 text-[#002B49] font-medium">
                            <Calendar size={12} />
                            {fmtDate(it.next_billing_time)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-5 py-4 text-[10px] font-mono text-[#64748B] truncate max-w-[120px]">
                        {it.paypal_subscription_id}
                      </td>
                      <td className="px-5 py-4 text-right">
                        {isTerminal ? (
                          <span className="text-xs text-[#64748B]">—</span>
                        ) : (
                          <button
                            onClick={() => cancel(it)}
                            disabled={cancelling === it.paypal_subscription_id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-red-700 hover:bg-red-50 border border-red-200 disabled:opacity-50"
                            data-testid={`sub-cancel-${i}`}
                          >
                            <X size={12} />
                            {cancelling === it.paypal_subscription_id ? "..." : "Annuler"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-[#64748B] mt-5 flex items-start gap-2 max-w-2xl">
        <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
        L'annulation est appliquée immédiatement chez PayPal. Le donateur ne sera plus prélevé. Les paiements déjà effectués sont conservés en historique.
      </p>
    </div>
  );
};

const KpiSmall = ({ label, value, testid }) => (
  <div className="bg-white rounded-xl border border-black/5 p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-medium">{label}</div>
    <div className="font-serif text-2xl text-[#002B49] mt-1.5 font-semibold">{value}</div>
  </div>
);

export default SubscriptionsManager;
